<template lang="html">
    <component
        :is="showLabel ? 'h6' : 'div'"
        v-if="journeyReferenceId"
        class="journey-ref-root d-flex align-items-center flex-wrap gap-1"
        :class="{ propno: showLabel }"
    >
        <span v-if="showLabel" class="journey-ref-label">{{ label }}</span>
        <span v-if="showJourneyReferenceActionMenu" class="journey-ref-menu-wrap d-inline-flex align-items-center flex-wrap gap-1">
            <button
                type="button"
                class="journey-ref-link"
                title="Journey reference actions"
                aria-haspopup="true"
                aria-controls="journey_action_menu"
                @click.stop="openJourneyMenu($event)"
            >{{ journeyReferenceId }}</button>
            <Menu
                ref="journeyActionMenu"
                id="journey_action_menu"
                :model="journeyMenuModel"
                :popup="true"
            />
        </span>
        <span v-else class="journey-ref-plain">{{ journeyReferenceId }}</span>
    </component>
</template>
<script>
import Menu from 'primevue/menu';
import {
    saveProposalPaymentRetrigger,
    fetchAndCopyResumeLink,
    DEFAULT_PAYMENT_RETRIGGER_STATUS_ID,
} from '@/utils/proposalPaymentRetrigger';

/** Health parity routes + motor surfaces where journey id is shown */
const JOURNEY_REFERENCE_MENU_PATHS = [
    '/payment',
    '/payment-status',
    '/proposal-submission',
    '/Thank-you-RM',
    '/inspection',
    '/process-wizard',
    '/policy-details',
    '/confirm-payment',
    '/confirm-payment-diy',
    '/fileUpload',
    '/upload-ovds'
];

/** PrimeVue ToastService (`app.use(ToastService)`) — `this.$toast.add` */
const TOAST_LIFE_MS = 4000;
const COPY_TOAST_LIFE_MS = 3000;
const RESUME_LINK_SUCCESS_SUMMARY = 'Resume link sent successfully';

export default {
    name: 'JourneyIdContainer',
    components: {
        Menu,
    },
    props: {
        /** When set, overrides Vuex journeyId (e.g. page loaders using sessionStorage). */
        journeyId: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: 'Reference number :',
        },
        showLabel: {
            type: Boolean,
            default: true,
        },
        paymentRetriggerStatusId: {
            type: Number,
            default: DEFAULT_PAYMENT_RETRIGGER_STATUS_ID,
        },
    },
    computed: {
        journeyReferenceId() {
            const fromProp = typeof this.journeyId === 'string' ? this.journeyId.trim() : '';
            if (fromProp) {
                return fromProp;
            }
            return this.$store.state.journeyId ?? '';
        },
        showJourneyReferenceActionMenu() {
            return JOURNEY_REFERENCE_MENU_PATHS.includes(this.$route.path);
        },
        journeyMenuModel() {
            return [
                {
                    label: 'Send journey to customer',
                    icon: 'pi pi-send',
                    disabled: this.proposalDiyLinkBusy,
                    command: () => this.sendJourneyToCustomer(),
                },
                {
                    label: 'Generate the Accept and Review Pay link',
                    icon: 'pi pi-link',
                    disabled: this.proposalDiyLinkBusy,
                    command: () => this.copyAcceptReviewPayLink(),
                },
                {
                    label: 'Copy the Reference number',
                    icon: 'pi pi-copy',
                    command: () => this.copyReferenceNumber(),
                },
            ];
        },
    },
    data() {
        return {
            proposalDiyLinkBusy: false,
        };
    },
    methods: {
        openJourneyMenu(event) {
            this.$refs.journeyActionMenu?.toggle(event);
        },
        addPrimeToast(payload) {
            if (this.$toast?.add) {
                this.$toast.add({
                    life: TOAST_LIFE_MS,
                    ...payload,
                });
            }
        },
        addCopyToast(text, detail) {
            if (this.$toast?.add) {
                this.$toast.add({
                    severity: 'success',
                    summary: text,
                    detail,
                    life: COPY_TOAST_LIFE_MS,
                });
            }
        },
        copyTextToClipboard(text, successDetail) {
            if (!text) {
                return Promise.reject(new Error('empty'));
            }
            if (navigator.clipboard?.writeText) {
                return navigator.clipboard.writeText(text).then(() => {
                    this.addCopyToast(text, successDetail);
                });
            }
            return new Promise((resolve, reject) => {
                try {
                    const tempInput = document.createElement('input');
                    tempInput.value = text;
                    document.body.appendChild(tempInput);
                    tempInput.select();
                    document.execCommand('copy');
                    document.body.removeChild(tempInput);
                    this.addCopyToast(text, successDetail);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        async copyAcceptReviewPayLink() {
            if (!this.journeyReferenceId || this.proposalDiyLinkBusy) {
                if (!this.journeyReferenceId) {
                    this.addPrimeToast({
                        severity: 'warn',
                        summary: 'Accept and Review Pay link',
                        detail: 'No journey reference available.',
                    });
                }
                return;
            }
            this.proposalDiyLinkBusy = true;
            try {
                const result = await fetchAndCopyResumeLink(
                    this.backendApi,
                    this.journeyReferenceId,
                );
                if (result.ok) {
                    this.addPrimeToast({
                        severity: 'success',
                        summary: 'Link copied to clipboard',
                    });
                    return;
                }
                const isMissingJourney = result.message === 'No journey reference available.';
                this.addPrimeToast({
                    severity: isMissingJourney ? 'warn' : 'error',
                    summary: isMissingJourney ? 'Accept and Review Pay link' : 'Copy failed',
                    detail: result.message || 'Could not copy link to clipboard.',
                });
            } finally {
                this.proposalDiyLinkBusy = false;
            }
        },
        copyReferenceNumber() {
            const id = this.journeyReferenceId;
            if (!id) {
                this.addPrimeToast({
                    severity: 'warn',
                    summary: 'Reference number',
                    detail: 'No journey reference available.',
                });
                return;
            }
            this.copyTextToClipboard(id, 'Journey Id Copied').catch(() => {
                this.addPrimeToast({
                    severity: 'error',
                    summary: 'Copy failed',
                    detail: 'Could not copy reference number.',
                });
            });
        },
        async sendJourneyToCustomer() {
            if (!this.journeyReferenceId || !this.showJourneyReferenceActionMenu || this.proposalDiyLinkBusy) {
                return;
            }
            this.proposalDiyLinkBusy = true;
            this.addPrimeToast({
                severity: 'info',
                summary: 'Sending the payment link',
                detail: 'Please wait…',
            });
            try {
                const response = await saveProposalPaymentRetrigger(
                    this.backendApi,
                    this.journeyReferenceId,
                    this.paymentRetriggerStatusId,
                );
                const errList = response?.data?.errors;
                const hasErrors = Array.isArray(errList) && errList.length > 0;
                if (hasErrors || response === 'error' || response?.status === 'error') {
                    const errDetail = errList?.[0]?.errorDisplayMessage || 'Unable to send payment link.';
                    this.addPrimeToast({
                        severity: 'error',
                        summary: 'Sending the payment link',
                        detail: errDetail,
                    });
                    return;
                }
                const apiMessage = response?.data?.data?.message;
                if (apiMessage) {
                    this.addPrimeToast({
                        severity: 'success',
                        summary: RESUME_LINK_SUCCESS_SUMMARY,
                        detail: apiMessage,
                    });
                    return;
                }
                this.addPrimeToast({
                    severity: 'warn',
                    summary: 'Sending the payment link',
                    detail: 'No message returned from the server.',
                });
            } catch (error) {
                console.log('Error in JourneyIdContainer payment retrigger: ', error);
                this.addPrimeToast({
                    severity: 'error',
                    summary: 'Sending the payment link',
                    detail: 'Could not update proposal status.',
                });
            } finally {
                this.proposalDiyLinkBusy = false;
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.propno {
    font-weight: bold;
}

.journey-ref-link {
    cursor: pointer;
    text-decoration: underline;
    word-break: break-all;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: inherit;
    color: inherit;
    text-align: inherit;
}

.journey-ref-plain {
    word-break: break-all;
}
</style>
